// utils/helpers/index.js

const PHONE_NUMBER_REGEX = /^\d+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEBSITE_PROTOCOL_REGEX = /^https?:\/\//i;

/**
 * Removes non-numeric characters from a phone number while preserving leading zeroes
 *
 * @param {string} value - Phone number input
 * @returns {string} Digits-only phone number
 */
export const sanitizePhoneNumber = (value = '') => value.replace(/\D/g, '');

/**
 * Checks whether a phone number contains digits only
 *
 * @param {string} value - Phone number to validate
 * @returns {boolean} Whether the phone number is valid
 */
export const isValidPhoneNumber = (value = '') => PHONE_NUMBER_REGEX.test(value);

/**
 * Checks whether an email address has a valid basic structure
 *
 * @param {string} value - Email address to validate
 * @returns {boolean} Whether the email address is valid
 */
export const isValidEmail = (value = '') => EMAIL_REGEX.test(value.trim());

/**
 * Checks whether an optional website is a complete HTTP or HTTPS URL
 *
 * @param {string} value - Website URL to validate
 * @returns {boolean} Whether the website is empty or valid
 */
export const isValidWebsiteUrl = (value = '') => {
    if (!value) return true;
    if (!WEBSITE_PROTOCOL_REGEX.test(value)) return false;

    try {
        const url = new URL(value);
        return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname);
    } catch (error) {
        return false;
    }
};

export function validateDeploymentRequestForm(data) {
    const errors = [];

    // Required base fields
    const requiredFields = [
        'companyName',
        'subdomain',
        'deploymentContactFirstName',
        'deploymentContactLastName',
        'deploymentContactEmail',
        'deploymentContactPhone',
        'adminCorrespondenceName',
        'adminCorrespondenceEmail',
        'learnerCorrespondenceName',
        'learnerCorrespondenceEmail',
        'learnerCorrespondencePhone',
        'learnerRedirectionURL'
    ];

    // Check required fields
    requiredFields.forEach((field) => {
        if (data[field] === undefined || data[field] === null || data[field] === '') {
            errors.push(`${field} is required`);
        }
    });

    // Validate subdomain (letters, numbers, hyphens only)
    if (data.subdomain && !/^[a-zA-Z0-9-]+$/.test(data.subdomain)) {
        errors.push('Subdomain must only contain letters, numbers, and hyphens');
    }

    // Validate email formats
    if (data.deploymentContactEmail && !/\S+@\S+\.\S+/.test(data.deploymentContactEmail)) {
        errors.push('Deployment Contact Email must be a valid email');
    }
    if (data.adminCorrespondenceEmail && !/\S+@\S+\.\S+/.test(data.adminCorrespondenceEmail)) {
        errors.push('Admin Correspondence Email must be a valid email');
    }
    if (data.learnerCorrespondenceEmail && !/\S+@\S+\.\S+/.test(data.learnerCorrespondenceEmail)) {
        errors.push('Learner Correspondence Email must be a valid email');
    }

    // Validate phone numbers (basic check for format)
    const phoneRegex = /^[0-9+() -]*$/; // Adjust as needed for more strict validation
    if (data.deploymentContactPhone && !phoneRegex.test(data.deploymentContactPhone)) {
        errors.push('Deployment Contact Phone must be a valid phone number');
    }
    if (data.learnerCorrespondencePhone && !phoneRegex.test(data.learnerCorrespondencePhone)) {
        errors.push('Learner Correspondence Phone must be a valid phone number');
    }

    // Check if work or study is selected
    if (!data.work && !data.study) {
        errors.push('Please select RPL Robot Type');
    }

    // Conditional validations
    if (data.learnerReminderEmails === true) {
        if (!data.learnerMaxReminders) {
            errors.push('learnerMaxReminders is required when learnerReminderEmails is true');
        } else if (data.learnerMaxReminders < 0 || data.learnerMaxReminders > 5) {
            errors.push('learnerMaxReminders must be between 0 and 5');
        }
        if (!data.learnerReminderDays) {
            errors.push('learnerReminderDays is required when learnerReminderEmails is true');
        } else if (data.learnerReminderDays < 1 || data.learnerReminderDays > 30) {
            errors.push('learnerReminderDays must be between 1 and 30');
        }
    }

    if (data.notifyAfterFinalReminder === true) {
        if (!data.notifyIncompleteEmailAddress) {
            errors.push('notifyIncompleteEmailAddress is required when notifyAfterFinalReminder is true');
        } else if (!/\S+@\S+\.\S+/.test(data.notifyIncompleteEmailAddress)) {
            errors.push('notifyIncompleteEmailAddress must be a valid email');
        }
    }

    if (data.dailyQuizCompletionEmails === true) {
        if (!data.dailySummaryEmailAddress) {
            errors.push('dailySummaryEmailAddress is required when dailyQuizCompletionEmails is true');
        } else if (!/\S+@\S+\.\S+/.test(data.dailySummaryEmailAddress)) {
            errors.push('dailySummaryEmailAddress must be a valid email');
        }
    }

    if (data.immediateQuizCompletionEmails === true) {
        if (!data.immediateCompletionEmailAddress) {
            errors.push('immediateCompletionEmailAddress is required when immediateQuizCompletionEmails is true');
        } else if (!/\S+@\S+\.\S+/.test(data.immediateCompletionEmailAddress)) {
            errors.push('immediateCompletionEmailAddress must be a valid email');
        }
    }

    // Validate learnerRedirectionURL
    if (data.learnerRedirectionURL && !/^https?:\/\/[^\s]+$/.test(data.learnerRedirectionURL)) {
        errors.push('Learner Redirection URL must be a valid URL');
    }

    // Validate users array
    if (!Array.isArray(data.users) || data.users.length === 0) {
        errors.push('At least one user is required in the users array');
    } else {
        data.users.forEach((user, index) => {
            if (!user.name || !user.name.first || !user.name.last) {
                errors.push(`User ${index + 1}: First and last name are required`);
            }
            if (!user.email || !/\S+@\S+\.\S+/.test(user.email)) {
                errors.push(`User ${index + 1}: Email is required and must be a valid email`);
            }
            if (!user.role || !['Administrator', 'Enrolment Officer', 'Assessor', 'Compliance Manager', 'Auditor'].includes(user.role)) {
                errors.push(
                    `User ${index + 1}: Role is required and must be one of: Administrator, Enrolment Officer, Assessor, Compliance Manager, Auditor`
                );
            }
        });
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

export function convertToFormData(obj, formData = new FormData(), parentKey = '') {
    for (let key in obj) {
        // Use Object.prototype.hasOwnProperty.call() to avoid the eslint error
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            const formKey = parentKey ? `${parentKey}[${key}]` : key;

            // Skip handling of the 'file' key
            if (key === 'file') {
                formData.append(formKey, value); // Append file as-is
                continue; // Skip further processing for the 'file' key
            }

            if (value && typeof value === 'object' && !Array.isArray(value)) {
                // Recursively handle nested objects
                convertToFormData(value, formData, formKey);
            } else if (Array.isArray(value)) {
                // Handle arrays (flatten each item with the index)
                value.forEach((item, index) => {
                    convertToFormData(item, formData, `${formKey}[${index}]`);
                });
            } else {
                // Append the key-value pair to the FormData object
                formData.append(formKey, value);
            }
        }
    }
    return formData;
}

export function isValidSubdomain(subdomain) {
    // Regex explanation:
    // ^[a-z]               → must start with a lowercase letter
    // (?:[a-z0-9]|-(?!-))* → allow lowercase letters, numbers, or a dash,
    //                        but dash cannot be followed by another dash (no "--")
    // [a-z0-9]$            → must end with a letter or number (not a dash)
    const regex = /^[a-z](?:[a-z0-9]|-(?!-))*[a-z0-9]$/;

    if (subdomain !== '' && regex.test(subdomain)) {
        return {
            isValid: true
        };
    }

    if (subdomain.length < 3) {
        return {
            isValid: false,
            message: 'Subdomain must have at least 3 characters.'
        };
    }
    return {
        isValid: false,
        message:
            'Must start with a lowercase letter, contain only lowercase letters, numbers, and dashes, no consecutive or trailing dashes.'
    };
}

export const objectEqual = (a, b) => {
    if (a === b) return true;

    if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) {
        return false;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
        if (!keysB.includes(key) || !objectEqual(a[key], b[key])) {
            return false;
        }
    }

    return true;
};

export function validatePasswordChars(str) {
    const result = {
        status: true,
        uppercase: false,
        lowercase: false,
        special: false,
        number: false
    };

    // Check for lowercase letters
    if (/[a-z]/.test(str)) result.lowercase = true;

    // Check for uppercase letters
    if (/[A-Z]/.test(str)) result.uppercase = true;

    // Check for digits
    if (/\d/.test(str)) result.number = true;

    // Check for special characters
    if (/[\W_]/.test(str)) result.special = true;

    // Determine if all conditions are met
    result.status = result.uppercase && result.lowercase && result.special && result.number;

    return result;
}

export const formatEmptyValue = (value) => value || '--';
